import React, { useCallback, useRef, forwardRef, CSSProperties } from 'react';
import Notification from './NotificationItem';
import InfiniteLoader from 'react-window-infinite-loader';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { Loading } from '../../UI';
import { IAlertNotification } from '../../../types/model';

interface NotificationListProps {
  hasNextToken: boolean;
  isNextDataLoading: boolean;
  notificationsList: IAlertNotification[];
  loadMore: any;
}

export const PADDING_SIZE = 8;
const DEFAULT_NOTIFICATION_HEIGHT = 40;

const innerElementType = forwardRef<HTMLDivElement, { style: CSSProperties }>(
  ({ style, ...rest }, ref) => (
    <div
      ref={ref}
      style={{
        ...style,
        height: `${parseFloat(style.height as string) + PADDING_SIZE}px`,
      }}
      {...rest}
    />
  )
);

const NotificationsList: React.FC<NotificationListProps> = ({
  notificationsList,
  hasNextToken,
  isNextDataLoading,
  loadMore,
}) => {
  const itemCount = hasNextToken
    ? notificationsList.length + 1
    : notificationsList.length;

  const loadMoreNotifications = isNextDataLoading ? () => {} : loadMore;

  const isNotificationLoaded = (index) =>
    !hasNextToken || index < notificationsList.length;

  // Calculate the notification height
  const sizeMap = useRef({});
  const listRef = useRef<any>();

  const setSize = useCallback((index, size) => {
    sizeMap.current = { ...sizeMap.current, [index]: size };
    listRef?.current?.resetAfterIndex?.(index);
  }, []);

  const getSize = useCallback(function (index) {
    return sizeMap.current[index] || DEFAULT_NOTIFICATION_HEIGHT;
  }, []);

  return (
    <AutoSizer>
      {({ width, height }) => (
        <InfiniteLoader
          isItemLoaded={isNotificationLoaded}
          loadMoreItems={loadMoreNotifications}
          itemCount={itemCount}
          threshold={1}
        >
          {({ onItemsRendered, ref }) => (
            <List
              itemSize={getSize}
              useIsScrolling={true}
              estimatedItemSize={DEFAULT_NOTIFICATION_HEIGHT}
              height={height}
              width={width}
              itemCount={itemCount}
              onItemsRendered={onItemsRendered}
              ref={(e) => {
                ref(e);
                listRef.current = e;
              }}
              innerElementType={innerElementType}
            >
              {({ index, style }) => {
                let content;
                if (!isNotificationLoaded(index)) {
                  content = (
                    <div style={style} className={'flex justify-center w-full'}>
                      <Loading status={true} size={'small'} />
                    </div>
                  );
                } else {
                  content = (
                    <Notification
                      notification={notificationsList[index]}
                      style={style}
                      setSize={setSize}
                      index={index}
                    />
                  );
                }
                return content;
              }}
            </List>
          )}
        </InfiniteLoader>
      )}
    </AutoSizer>
  );
};

export default NotificationsList;
